import { Helmet } from "react-helmet-async";

const SITE_URL = "https://herfithub.com";
const DEFAULT_OG =
  "https://images.unsplash.com/photo-1552848031-326ec03fe2ec?crop=entropy&cs=srgb&fm=jpg&w=1200&h=630&fit=crop&q=80";

/**
 * Centralised SEO component used across every page.
 *
 * Outputs:
 *  - <title> & description
 *  - Canonical URL
 *  - OpenGraph (Facebook / WhatsApp / iMessage previews)
 *  - Twitter / X Cards
 *  - Optional JSON-LD structured data blocks (array of objects)
 */
export default function SEO({
  title,
  description,
  path = "/",
  image = DEFAULT_OG,
  type = "website",
  noIndex = false,
  jsonLd = [],
  keywords,
  publishedAt,
  modifiedAt,
}) {
  const url = `${SITE_URL}${path}`;
  const fullTitle = title?.includes("HerFitHub")
    ? title
    : `${title} | HerFitHub`;

  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      {keywords && <meta name="keywords" content={keywords} />}
      <link rel="canonical" href={url} />
      {noIndex && <meta name="robots" content="noindex,nofollow" />}

      {/* Open Graph */}
      <meta property="og:type" content={type} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={url} />
      <meta property="og:image" content={image} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:site_name" content="HerFitHub" />
      <meta property="og:locale" content="en_GB" />
      {publishedAt && <meta property="article:published_time" content={publishedAt} />}
      {modifiedAt && <meta property="article:modified_time" content={modifiedAt} />}

      {/* Twitter / X */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image} />

      {/* JSON-LD structured data */}
      {jsonLd.map((data, i) => (
        <script key={i} type="application/ld+json">
          {JSON.stringify(data)}
        </script>
      ))}
    </Helmet>
  );
}

/* ---------- Reusable JSON-LD builders ---------- */
export const SITE_NAME = "HerFitHub";
export const COACH_NAME = "Maya";
export const SITE_DESCRIPTION =
  "Women's strength coaching with Maya — London-based women's personal trainer offering online coaching for women, postpartum strength coaching and beginner gym coaching.";

export const websiteJsonLd = {
  "@context": "https://schema.org",
  "@type": "WebSite",
  name: SITE_NAME,
  url: SITE_URL,
  description: SITE_DESCRIPTION,
  potentialAction: {
    "@type": "SearchAction",
    target: `${SITE_URL}/blog?q={search_term_string}`,
    "query-input": "required name=search_term_string",
  },
};

export const localBusinessJsonLd = {
  "@context": "https://schema.org",
  "@type": "HealthAndBeautyBusiness",
  name: SITE_NAME,
  alternateName: "HerFitHub — Women's Strength Coaching with Maya",
  description: SITE_DESCRIPTION,
  url: SITE_URL,
  image: DEFAULT_OG,
  priceRange: "££",
  email: "hello@herfithub.com",
  areaServed: [
    { "@type": "City", name: "London" },
    { "@type": "AdministrativeArea", name: "United Kingdom" },
    { "@type": "Place", name: "Worldwide (Online Coaching)" },
  ],
  address: {
    "@type": "PostalAddress",
    addressLocality: "London",
    addressCountry: "GB",
  },
  sameAs: [
    "https://instagram.com/strongwithmaya",
    "https://instagram.com/herfithub.co",
  ],
  knowsAbout: [
    "Women's strength coaching",
    "Women's personal trainer London",
    "Online coaching for women",
    "Beginner gym coaching for women",
    "Postpartum strength coaching",
    "Strength training for women",
  ],
};

export const personJsonLd = {
  "@context": "https://schema.org",
  "@type": "Person",
  name: COACH_NAME,
  jobTitle: "Women's Strength Coach",
  description:
    "London-based women's strength coach helping women build strength, confidence and consistency through realistic, beginner-friendly training.",
  url: `${SITE_URL}/about`,
  image: DEFAULT_OG,
  worksFor: { "@type": "Organization", name: SITE_NAME, url: SITE_URL },
  knowsAbout: [
    "Women's strength training",
    "Postpartum strength coaching",
    "Beginner gym coaching for women",
    "Online coaching for women",
  ],
  sameAs: [
    "https://instagram.com/strongwithmaya",
    "https://instagram.com/herfithub.co",
  ],
};

export function faqJsonLd(items) {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: items.map((i) => ({
      "@type": "Question",
      name: i.q,
      acceptedAnswer: { "@type": "Answer", text: i.a },
    })),
  };
}

export function articleJsonLd(post) {
  return {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    headline: post.title,
    description: post.excerpt,
    image: post.image_url,
    datePublished: post.published_at,
    dateModified: post.published_at,
    articleSection: post.category,
    author: {
      "@type": "Person",
      name: post.author || COACH_NAME,
      url: `${SITE_URL}/about`,
    },
    publisher: {
      "@type": "Organization",
      name: SITE_NAME,
      logo: { "@type": "ImageObject", url: `${SITE_URL}/favicon.svg` },
    },
    mainEntityOfPage: `${SITE_URL}/blog/${post.slug}`,
  };
}

export function breadcrumbJsonLd(items) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((it, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: it.name,
      item: it.url ? `${SITE_URL}${it.url}` : undefined,
    })),
  };
}
